import { doctorScenarios } from "./doctor";
import { policeScenarios } from "./police";
import type { GameScenario } from "./types";

export type GameOutcome = {
  minFail: number;
  maxFail: number;
  title: string;
  descriptionTitle: string;
  description: string;
  video?: string;
};

export type GamePhase = "intro" | "selection" | "result" | "complete";

export type GameConfig = {
  id: string;
  name: string;
  heroTitle: string;
  description: string;
  heroEmoji: string;
  selectionCta: string;
  badgeLabel: string;
  introVideo?: string;
  introVideoPoster?: string;
  scenarios: GameScenario[];
  successLabel: string;
  failLabel: string;
  outcomes: GameOutcome[];
};

export const GAME_LIBRARY: Record<string, GameConfig> = {
  police: {
    id: "police",
    name: "긴급 미션! 강남 한복판 초대형 보이스피싱 보스 검거 작전!",
    heroTitle: "긴급 미션! 강남 한복판 초대형<br/> 보이스피싱 보스 검거 작전!",
    description: "AI 수사 기술로 초대형 보이스피싱 조직을 검거하세요.",
    heroEmoji: "🚨",
    selectionCta: "수사 작전 투입",
    badgeLabel: "Special Mission Briefing",
    introVideo: "./videos/police-mission/경찰 인트로.mp4",
    introVideoPoster: "./images/police-thumbnail.png",
    scenarios: policeScenarios,
    successLabel: "AI 전략",
    failLabel: "기타 선택",
    outcomes: [
      {
        minFail: 4,
        maxFail: 5,
        title: "동 등급 (Bronze)",
        descriptionTitle: "🥉 미션 성공! 동 등급을 획득했습니다!",
        description:
          "당신의 끈기와 노력 덕분에 결국 총책을 체포했어요. 하지만 작전이 너무 늦어져 중요한 증거를 많이 잃었고, 피해 금액도 일부밖에 되찾지 못했어요.",
        video: "./videos/police-mission/결과3 간신히 성공.mp4",
      },
      {
        minFail: 2,
        maxFail: 3,
        title: "은 등급 (Silver)",
        descriptionTitle: "🥈 훌륭한 성공! 은 등급을 획득했습니다!",
        description:
          "당신은 총책 검거에는 성공했지만, 중간에 핵심 증거를 일부 놓치고 말았어요. 다음번에는 완벽한 성공을 만들어 보세요!",
        video: "./videos/police-mission/결과2 아쉬운 성공.mp4",
      },
      {
        minFail: 0,
        maxFail: 1,
        title: "금 등급 (Gold)",
        descriptionTitle: "🥇 완벽한 성공! 금 등급을 획득했습니다!",
        description:
          "당신은 5단계 내내 가장 빠르고 정확한 수사 방식을 선택했어요! 단 하나의 실수도 없이 총책을 체포했고, 모든 증거를 완벽하게 지켜냈습니다. 미래의 경찰관이 갖춰야 할 최고의 수사 능력을 보여주셨습니다!",
        video: "./videos/police-mission/결과1 대성공.mp4",
      },
    ],
  },
  doctor: {
    id: "doctor",
    name: "응급실 히어로! 5단계 미스터리 병마 '골든타임 사수 작전'",
    heroTitle: "응급실 히어로! 5단계 미스터리 병마<br/> '골든타임 사수 작전'",
    description: "AI 메디컬 솔루션으로 골든타임을 지켜 환자를 살려내세요.",
    heroEmoji: "💊",
    selectionCta: "치료 작전 투입",
    badgeLabel: "Future Care Protocol",
    introVideo: "./videos/doctor-mission/의사 인트로.mp4",
    introVideoPoster: "./images/doctor-thumbnail.png",
    scenarios: doctorScenarios,
    successLabel: "AI 치료 전략",
    failLabel: "기타 선택",
    outcomes: [
      {
        minFail: 4,
        maxFail: 5,
        title: "동 등급 (Gold)",
        descriptionTitle: "🥉 미션 성공! 동 등급을 획득했습니다!",
        description:
          "당신의 끈기 있는 노력 덕분에 치료는 성공했어요. 하지만 진료 업무에 쫓겨 환자에게 충분한 공감과 지지를 해주지 못했어요. 환자는 치료는 끝났지만, 심리적으로 지치고 불안한 상태가 남았습니다.",
        video: "./videos/doctor-mission/의사 미션 아슬아슬성공.mp4",
      },
      {
        minFail: 2,
        maxFail: 3,
        title: "은 등급 (Silver)",
        descriptionTitle: "🥈 훌륭한 성공! 은 등급을 획득했습니다!",
        description:
          "환자의 치료는 무사히 마쳤지만 의사가 더 많은 시간을 진료에 쓰느라 환자의 심리적 케어에는 소홀했어요. 다음에는 더 많은 시간을 환자에게 집중해주세요!",
        video: "./videos/doctor-mission/의사 미션 아쉬운 성공.mp4",
      },
      {
        minFail: 0,
        maxFail: 1,
        title: "금 등급 (Gold)",
        descriptionTitle: "🥇 완벽한 성공! 금 등급을 획득했습니다!",
        description:
          "당신은 빠르고 정확한 데이터로 치료 시간을 벌었고, 마지막에는 그 시간을 오직 환자의 마음을 치료하는 데 사용했어요. 미래 의사에게 필요한 지식(AI 활용)과 공감(휴머니즘)을 모두 갖춘 훌륭한 의사입니다!",
        video: "./videos/doctor-mission/의사 미션 대성공.mp4",
      },
    ],
  },
};

export const GAME_OPTIONS = Object.values(GAME_LIBRARY);

const shuffleChoices = <T>(choices: T[]): T[] => {
  const shuffled = [...choices];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const prepareScenarios = (scenarioList: GameScenario[]) =>
  scenarioList.map((scenario) => ({
    ...scenario,
    choices: shuffleChoices(scenario.choices),
  }));

export const resolveGameOutcome = (
  gameType: string,
  stats: { fail: number }
) => {
  const config = GAME_LIBRARY[gameType];
  if (!config) {
    return null;
  }

  const { outcomes } = config;
  const fallback = outcomes[outcomes.length - 1];
  return (
    outcomes.find(
      ({ minFail, maxFail }) =>
        typeof minFail === "number" &&
        typeof maxFail === "number" &&
        stats.fail >= minFail &&
        stats.fail <= maxFail
    ) ?? fallback
  );
};

export const stripLineBreakTags = (text: string | undefined) =>
  typeof text === "string" ? text.replace(/<br\s*\/?>(\s*)/gi, " ") : text;
